/**
 * Typed client for the Phase 3 AI endpoints (packages/protocol/src/api-ai.ts).
 * Lives inside the ai feature so the shared src/api.ts stays untouched.
 *
 * Deep analyses return either the artifact (200, cached) or a DeepAnalysisJob
 * (202), so the client exposes the status explicitly. Chat streams SSE.
 */
import type {
  AiCapability,
  AiSettings,
  AiUsage,
  ChatEvent,
  ChatRequest,
  ClusterExplanation,
  Conversation,
  ConversationList,
  DeepAnalysisJob,
  Digest,
  ExplainArtifact,
  ExplainRequest,
  FunnelExplanation,
  Page,
  PrDraftArtifact,
  ReproArtifact,
  SearchResult,
  SessionSummaryResponse,
  Transcript,
} from "@backstory/protocol";
import { API_URL, ApiRequestError, CSRF_HEADER, DEV_TOKEN, PROJECT_HEADER, UNAUTHORIZED_EVENT, currentCsrf, getApiProject, networkError, request, responseError } from "../../api";
import { trackPending } from "../../lib/pending";

const enc = encodeURIComponent;

/** Result of a deep-analysis request: artifact when cached, job when queued. */
export type DeepResult<T> = { kind: "artifact"; artifact: T } | { kind: "job"; job: DeepAnalysisJob };

function baseHeaders(method: string, hasBody: boolean): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (hasBody) headers["Content-Type"] = "application/json";
  if (method !== "GET") {
    const csrf = currentCsrf();
    if (csrf) headers[CSRF_HEADER] = csrf;
  }
  const project = getApiProject();
  if (project) headers[PROJECT_HEADER] = project;
  if (DEV_TOKEN) headers.Authorization = `Bearer ${DEV_TOKEN}`;
  return headers;
}

async function throwFor(res: Response, path: string): Promise<never> {
  let body: Record<string, unknown> = {};
  try {
    body = (await res.json()) as Record<string, unknown>;
  } catch {
    /* non-JSON */
  }
  if (res.status === 401 && typeof window !== "undefined") window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: { path } }));
  throw responseError(res, body);
}

/** POST that distinguishes 200 (artifact) from 202 (job). A 200 body that looks like a job is treated as a job. */
export function deepRequest<T>(path: string, body: unknown): Promise<DeepResult<T>> {
  return trackPending(sendDeep<T>(path, body));
}

async function sendDeep<T>(path: string, body: unknown): Promise<DeepResult<T>> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { method: "POST", headers: baseHeaders("POST", true), credentials: "include", body: JSON.stringify(body ?? {}) });
  } catch (e) {
    throw networkError(e);
  }
  if (!res.ok) await throwFor(res, path);
  const data = (await res.json()) as unknown;
  if (res.status === 202 || isJob(data)) return { kind: "job", job: data as DeepAnalysisJob };
  return { kind: "artifact", artifact: data as T };
}

export function isJob(v: unknown): v is DeepAnalysisJob {
  return !!v && typeof v === "object" && "status" in (v as object) && "units" in (v as object) && !("meta" in (v as object));
}

/** Parse a `text/event-stream` body into ChatEvents. Exported for tests. */
export async function* parseSse(body: ReadableStream<Uint8Array>, signal?: AbortSignal): AsyncGenerator<ChatEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  try {
    for (;;) {
      if (signal?.aborted) return;
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buf.indexOf("\n\n")) >= 0) {
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const ev = parseFrame(frame);
        if (ev) yield ev;
      }
    }
    const tail = parseFrame(buf);
    if (tail) yield tail;
  } finally {
    reader.releaseLock();
  }
}

function parseFrame(frame: string): ChatEvent | null {
  const dataLines = frame
    .split("\n")
    .filter((l) => l.startsWith("data:"))
    .map((l) => l.slice(5).trimStart());
  if (!dataLines.length) return null;
  const raw = dataLines.join("\n");
  if (raw === "[DONE]") return null;
  try {
    return JSON.parse(raw) as ChatEvent;
  } catch {
    return { type: "error", message: `malformed event: ${raw.slice(0, 80)}` };
  }
}

export const aiApi = {
  /** The stored summary plus what the pipeline is currently doing about it. */
  summary: (sessionId: string) => request<SessionSummaryResponse>(`/v1/sessions/${enc(sessionId)}/summary`),
  /** Queue a replacement for the stored summary. Progress comes back through `summary`. */
  resummarize: (sessionId: string) => request<{ status: string }>(`/v1/sessions/${enc(sessionId)}/summary`, { method: "POST" }),
  transcript: (sessionId: string) => request<Transcript>(`/v1/sessions/${enc(sessionId)}/transcript`),
  explain: (sessionId: string, req: ExplainRequest) => deepRequest<ExplainArtifact>(`/v1/sessions/${enc(sessionId)}/explain`, req),
  repro: (sessionId: string, range?: { fromMs?: number; toMs?: number }) => deepRequest<ReproArtifact>(`/v1/sessions/${enc(sessionId)}/repro`, range ?? {}),
  explainCluster: (clusterId: string) => deepRequest<ClusterExplanation>(`/v1/issues/${enc(clusterId)}/explain`, {}),
  draftPr: (clusterId: string, repo: string) => deepRequest<PrDraftArtifact>(`/v1/issues/${enc(clusterId)}/draft-pr`, { repo }),
  /** The saved explanation for this exact funnel and reporting range. */
  funnelExplanation: async (funnelId: string, range?: { from?: string; to?: string }): Promise<FunnelExplanation | null> => {
    const query = [range?.from && `from=${enc(range.from)}`, range?.to && `to=${enc(range.to)}`].filter(Boolean).join("&");
    try {
      return await request<FunnelExplanation>(`/v1/funnels/${enc(funnelId)}/explain${query ? `?${query}` : ""}`);
    } catch (e) {
      if (e instanceof ApiRequestError && e.status === 404) return null;
      throw e;
    }
  },
  /** A first analysis uses cache; force replaces the saved artifact after success. */
  explainFunnel: (funnelId: string, range?: { from?: string; to?: string }, force = false) =>
    deepRequest<FunnelExplanation>(`/v1/funnels/${enc(funnelId)}/explain`, { ...(range ?? {}), force }),
  job: (id: string) => request<DeepAnalysisJob>(`/v1/ai/jobs/${enc(id)}`),
  /** Fetch an artifact by the job's resultUrl (server-relative path). */
  result: <T>(resultUrl: string) => request<T>(resultUrl.startsWith("/") ? resultUrl : `/${resultUrl}`),
  feedback: (artifactId: string, rating: "up" | "down" | "", reason?: string) =>
    request<void>(`/v1/ai/feedback`, { method: "POST", body: { artifactId, rating, reason } }),
  search: (q: string, opts?: { limit?: number; cursor?: string; filters?: Record<string, unknown> }) =>
    request<SearchResult>(`/v1/search`, { method: "POST", body: { q, limit: opts?.limit ?? 50, cursor: opts?.cursor, filters: opts?.filters } }),
  digests: (cursor?: string) => request<Page<Digest>>(`/v1/ai/digests${cursor ? `?cursor=${enc(cursor)}` : ""}`),
  deleteDigest: (id: string) => request<void>(`/v1/ai/digests/${enc(id)}`, { method: "DELETE" }),
  usage: (from?: string, to?: string) => request<AiUsage>(`/v1/ai/usage${from || to ? `?${[from && `from=${enc(from)}`, to && `to=${enc(to)}`].filter(Boolean).join("&")}` : ""}`),
  capability: () => request<AiCapability>(`/v1/ai/capability`),
  settings: (orgId: string) => request<AiSettings>(`/v1/orgs/${enc(orgId)}/ai/settings`),
  patchSettings: (orgId: string, patch: AiSettingsPatch) => request<AiSettings>(`/v1/orgs/${enc(orgId)}/ai/settings`, { method: "PATCH", body: patch }),
  /** One page of a thread, newest first page; `cursor` walks back into older messages. */
  conversation: (id: string, cursor?: string) => request<Conversation>(`/v1/ai/chat/${enc(id)}${cursor ? `?cursor=${enc(cursor)}` : ""}`),
  /** The caller's most recent thread for this context, if there is one. An empty context is the project-wide chat. */
  latestConversation: (ctx: { sessionId?: string; clusterId?: string }) =>
    request<{ conversation: Conversation | null }>(
      `/v1/ai/conversations?${[ctx.sessionId && `sessionId=${enc(ctx.sessionId)}`, ctx.clusterId && `clusterId=${enc(ctx.clusterId)}`].filter(Boolean).join("&")}`,
    ),
  /** Earlier chats for this context, newest activity first. */
  conversationHistory: (ctx: { sessionId?: string; clusterId?: string; cursor?: string }) =>
    request<ConversationList>(
      `/v1/ai/conversations/history?${[ctx.sessionId && `sessionId=${enc(ctx.sessionId)}`, ctx.clusterId && `clusterId=${enc(ctx.clusterId)}`, ctx.cursor && `cursor=${enc(ctx.cursor)}`].filter(Boolean).join("&")}`,
    ),
  deleteConversation: (id: string) => request<void>(`/v1/ai/chat/${enc(id)}`, { method: "DELETE" }),

  /** Streamed chat. Yields ChatEvents until `done` or `error`; abort via the signal. */
  async *chat(req: ChatRequest, signal?: AbortSignal): AsyncGenerator<ChatEvent> {
    let res: Response;
    try {
      res = await fetch(`${API_URL}/v1/ai/chat`, {
        method: "POST",
        headers: { ...baseHeaders("POST", true), Accept: "text/event-stream" },
        credentials: "include",
        body: JSON.stringify(req),
        signal,
      });
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      throw networkError(e);
    }
    if (!res.ok) await throwFor(res, "/v1/ai/chat");
    if (!res.body) {
      // Non-streaming fallback: a JSON array of events.
      const arr = (await res.json()) as ChatEvent[];
      for (const ev of arr) yield ev;
      return;
    }
    yield* parseSse(res.body, signal);
  },
};

/**
 * Customer-editable settings. Which AI source an organization uses is read here
 * but switched through the scoped admin endpoint, which can refuse the change
 * when the organization's own routes are incomplete, so it is not patchable.
 */
export type AiSettingsPatch = Partial<Omit<AiSettings, "routingMode" | "organizationRouting" | "integrations">> & {
  github?: { repositories?: string[]; clear?: boolean };
};

/** Settings plus the repository list older responses used for the draft default. */
export type AiSettingsExt = Omit<AiSettings, "integrations"> & {
  integrations?: { github?: { connected: boolean; repositories?: string[]; available?: string[] | null; repos?: string[]; connectUrl?: string } };
};

export function savedGitHubRepositories(settings: AiSettingsExt | undefined): string[] {
  const github = settings?.integrations?.github;
  if (github?.repositories && github.repositories.length > 0) return github.repositories;
  return github?.repos ?? [];
}

export const aiKeys = {
  capability: () => ["ai", "capability"] as const,
  summary: (id: string) => ["ai", "summary", id] as const,
  transcript: (id: string) => ["ai", "transcript", id] as const,
  funnelExplanation: (id: string, range: { from?: string; to?: string }) => ["ai", "funnelExplanation", id, range] as const,
  usage: () => ["ai", "usage"] as const,
  settings: (org: string) => ["ai", "settings", org] as const,
  digests: () => ["ai", "digests"] as const,
  conversation: (id: string) => ["ai", "conversation", id] as const,
  sessionIssues: (id: string) => ["issues", id] as const,
};

/**
 * The one sentence every disabled AI control says. It is a single constant
 * because it appears on dozens of buttons and tests assert it verbatim.
 */
export const AI_UNCONFIGURED = "AI isn't configured yet";

/** Human wording for a rejected deep analysis. */
export function rejectedMessage(reason: DeepAnalysisJob["rejectedReason"] | undefined): { title: string; body: string; action?: "usage" | "settings" } {
  switch (reason) {
    case "allotment_exhausted":
      return { title: "AI credits are used up", body: "Ask an owner or admin to add more credits.", action: "usage" };
    case "spend_cap":
      return { title: "AI spending limit reached", body: "Ask an owner or admin to raise it.", action: "settings" };
    case "ai_disabled":
      return { title: "AI is turned off", body: "Ask an owner or admin to turn it on.", action: "settings" };
    case "platform_unconfigured":
      return { title: AI_UNCONFIGURED, body: "Nobody has connected an AI provider to this deployment yet." };
    case "organization_routing_unconfigured":
      return { title: AI_UNCONFIGURED, body: "Your organization is set to use its own providers, but its model routing is incomplete.", action: "settings" };
    case "phi_provider_unavailable":
      return { title: "AI is unavailable", body: "Try again later." };
    default:
      return { title: "Analysis rejected", body: "The request could not be run. Try again later." };
  }
}
